import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getEntityTypes, getEntitiesByType, getAllEntities, createEntity, createEntityType,
    type EntityType, type Entity,
} from '../../packages/api-client/entities-api';
import { usePageTitle } from '../../hooks/usePageTitle';
import { PageHeader, StateView } from '../../packages/ui';
import { Globe, Plus, X, ClipboardList } from 'lucide-react';
import { hexAlpha } from '../../utils/statusColors';
import { EntityIcon, PICKER_ICONS } from '../../components/taskIcons';

export function EntitiesPage() {
    usePageTitle('Records');
    const navigate = useNavigate();
    const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
    const [entities, setEntities] = useState<Entity[]>([]);
    const [selectedType, setSelectedType] = useState<EntityType | null>(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [entitiesLoading, setEntitiesLoading] = useState(true);
    const [typesFailed, setTypesFailed] = useState(false);
    const [entitiesFailed, setEntitiesFailed] = useState(false);
    const [showEntityModal, setShowEntityModal] = useState(false);
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [entityForm, setEntityForm] = useState({ name: '', description: '', entity_type_id: '' });
    const [typeForm, setTypeForm] = useState({ name: '', slug: '', icon: 'folder', color: '#14867A' });
    const [creating, setCreating] = useState(false);

    const loadTypes = useCallback(() => {
        setLoading(true);
        setTypesFailed(false);
        getEntityTypes()
            .then(setEntityTypes)
            .catch(() => setTypesFailed(true))
            .finally(() => setLoading(false));
    }, []);
    useEffect(loadTypes, [loadTypes]);

    const loadEntities = useCallback(() => {
        setEntitiesLoading(true);
        setEntitiesFailed(false);
        const fetch = selectedType
            ? getEntitiesByType(selectedType.id)
            : getAllEntities();
        fetch.then(result => {
            setEntities(result);
        }).catch(() => setEntitiesFailed(true))
            .finally(() => setEntitiesLoading(false));
    }, [selectedType]);
    useEffect(loadEntities, [loadEntities]);

    const filtered = entities.filter(e =>
        e.name.toLowerCase().includes(search.toLowerCase())
    );

    async function handleCreateEntity(ev: React.FormEvent) {
        ev.preventDefault();
        setCreating(true);
        try {
            const e = await createEntity({
                ...entityForm,
                entity_type_id: entityForm.entity_type_id || selectedType?.id,
            });
            setEntities(prev => [e, ...prev]);
            setShowEntityModal(false);
            setEntityForm({ name: '', description: '', entity_type_id: '' });
        } finally { setCreating(false); }
    }

    async function handleCreateType(ev: React.FormEvent) {
        ev.preventDefault();
        setCreating(true);
        try {
            const t = await createEntityType(typeForm);
            setEntityTypes(prev => [...prev, t]);
            setShowTypeModal(false);
            setTypeForm({ name: '', slug: '', icon: 'folder', color: '#14867A' });
        } finally { setCreating(false); }
    }

    return (
        <div style={{ display: 'flex', gap: 'var(--space-6)', minHeight: 'calc(100vh - 120px)' }}>
            {/* Left sidebar: entity types */}
            <aside style={{ width: 220, flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
                    Record Types
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                    <button
                        onClick={() => setSelectedType(null)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                            padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                            background: !selectedType ? 'var(--color-primary-glow)' : 'transparent',
                            color: !selectedType ? 'var(--color-primary)' : 'var(--color-text-muted)',
                            border: 'none', cursor: 'pointer', fontWeight: !selectedType ? 600 : 400, width: '100%', textAlign: 'left',
                        }}>
                        <Globe size={16} aria-hidden="true" /> All Records
                    </button>
                    {typesFailed && (
                        <StateView state="error" title="Record types couldn't be loaded" message="Records are still listed." onRetry={loadTypes} />
                    )}
                    {loading
                        ? <StateView state="loading" label="Loading record types" />
                        : entityTypes.map(type => (
                            <button
                                key={type.id}
                                onClick={() => setSelectedType(type)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                                    padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                                    background: selectedType?.id === type.id ? hexAlpha(type.color, 0.13) : 'transparent',
                                    color: selectedType?.id === type.id ? type.color : 'var(--color-text-muted)',
                                    border: 'none', cursor: 'pointer',
                                    fontWeight: selectedType?.id === type.id ? 600 : 400,
                                    width: '100%', textAlign: 'left', fontSize: 'var(--font-size-sm)',
                                }}>
                                <EntityIcon icon={type.icon} size={14} /> {type.name}
                            </button>
                        ))
                    }
                    <button className="btn btn-ghost btn-sm" style={{ marginTop: 'var(--space-2)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }} onClick={() => setShowTypeModal(true)}>
                        <Plus size={14} aria-hidden="true" /> New Type
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <div style={{ flex: 1 }}>
                <PageHeader
                    title={selectedType
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}><EntityIcon icon={selectedType.icon} size={20} /> {selectedType.name}</span>
                        : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}><Globe size={22} aria-hidden="true" /> All records</span>}
                    subtitle={entitiesLoading || entitiesFailed ? undefined : `${filtered.length} record${filtered.length !== 1 ? 's' : ''}`}
                    actions={<>
                        <input type="search" className="form-input" style={{ width: 200 }} placeholder="Search…" aria-label="Search records" value={search} onChange={e => setSearch(e.target.value)} />
                        <button className="btn btn-primary btn-md" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }} onClick={() => setShowEntityModal(true)}><Plus size={16} aria-hidden="true" /> New Record</button>
                    </>}
                />

                {entitiesFailed && <StateView state="error" message="Records could not be loaded. Check your connection and retry." onRetry={loadEntities} />}
                <div className="projects-grid">
                    {entitiesLoading && (
                        <div style={{ gridColumn: '1 / -1' }}><StateView state="loading" label="Loading records" lines={4} /></div>
                    )}
                    {!entitiesLoading && filtered.map(entity => (
                        <div key={entity.id} className="project-card" onClick={() => navigate(`/entities/${entity.id}`)}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: entity.entity_type_color, borderRadius: '0.5rem 0.5rem 0 0' }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                                <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: 'var(--radius-md)', background: hexAlpha(entity.entity_type_color, 0.13), color: entity.entity_type_color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                    <EntityIcon icon={entity.entity_type_icon} size={18} />
                                </div>
                                <div>
                                    <div className="project-card-name" style={{ marginBottom: 0 }}>{entity.name}</div>
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: entity.entity_type_color, fontWeight: 600 }}>{entity.entity_type_name}</div>
                                </div>
                            </div>
                            {entity.description && <div className="project-card-desc">{entity.description}</div>}
                            <div className="project-card-meta">
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                                    <ClipboardList size={12} aria-hidden="true" /> {entity.task_count} task{entity.task_count !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    ))}
                    {filtered.length === 0 && !entitiesLoading && !entitiesFailed && (
                        <div style={{ gridColumn: '1 / -1' }}>
                            {search && entities.length > 0
                                ? <StateView state="filtered-empty" message={`No record name matches "${search}".`} onClearFilters={() => setSearch('')} />
                                : <StateView state="empty" title="No records yet" message="Create your first record to start linking tasks." />}
                        </div>
                    )}
                </div>
            </div>

            {/* New Entity Modal */}
            {showEntityModal && (
                <div className="modal-overlay" onClick={() => setShowEntityModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <span className="modal-title">New Record</span>
                            <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowEntityModal(false)}><X size={16} aria-hidden="true" /></button>
                        </div>
                        <form onSubmit={handleCreateEntity}>
                            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div className="form-group">
                            <label className="form-label">Record Type *</label>
                                    <select className="form-input" required
                                        value={entityForm.entity_type_id || selectedType?.id || ''}
                                        onChange={e => setEntityForm(f => ({ ...f, entity_type_id: e.target.value }))}>
                                        <option value="">— Select type —</option>
                                        {entityTypes.map(t => <option key={t.id} value={t.id}>{PICKER_ICONS[t.icon] ? t.name : `${t.icon} ${t.name}`}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Name *</label>
                                    <input className="form-input" required placeholder="Acme Corp" value={entityForm.name}
                                        onChange={e => setEntityForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <input className="form-input" placeholder="Optional description" value={entityForm.description}
                                        onChange={e => setEntityForm(f => ({ ...f, description: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowEntityModal(false)}>Cancel</button>
                                <button className="btn btn-primary btn-md" type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* New Type Modal */}
            {showTypeModal && (
                <div className="modal-overlay" onClick={() => setShowTypeModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <span className="modal-title">New Record Type</span>
                            <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setShowTypeModal(false)}><X size={16} aria-hidden="true" /></button>
                        </div>
                        <form onSubmit={handleCreateType}>
                            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div className="form-group">
                                    <label className="form-label">Name *</label>
                                    <input className="form-input" required placeholder="e.g. Warehouse" value={typeForm.name}
                                        onChange={e => setTypeForm(f => ({ ...f, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="form-group">
                                        <label className="form-label">Icon</label>
                                        <input className="form-input" placeholder="🏭" value={typeForm.icon}
                                            onChange={e => setTypeForm(f => ({ ...f, icon: e.target.value }))} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Color</label>
                                        <input type="color" className="form-input" style={{ height: 40, padding: 4 }} value={typeForm.color}
                                            onChange={e => setTypeForm(f => ({ ...f, color: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary btn-md" type="button" onClick={() => setShowTypeModal(false)}>Cancel</button>
                                <button className="btn btn-primary btn-md" type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create Type'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
